import { AxiosError } from 'axios';

import { queryClient } from './query-client';

// Error types
export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  RATE_LIMIT = 'RATE_LIMIT',
  SUBSCRIPTION_LIMIT = 'SUBSCRIPTION_LIMIT',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN',
}

export interface AppError {
  type: ErrorType;
  message: string;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
  timestamp: string;
  requestId?: string;
  path?: string;
  method?: string;
}

export interface ValidationError {
  field: string;
  message: string;
  value?: unknown;
}

/**
 * NestJS ProblemDetails entry shape — every offending property gets a
 * `constraints` object whose values are the human-readable rejection
 * messages. Nested DTOs (e.g. `documents.license`) surface as `children`,
 * each with the same recursive shape.
 */
interface ProblemDetailsValidationEntry {
  property?: string;
  constraints?: Record<string, string>;
  children?: ProblemDetailsValidationEntry[];
}

/**
 * Walks the ProblemDetails `extensions.validationErrors` tree and returns
 * the flattened list of constraint messages plus a single joined string
 * for use as the user-facing AppError.message. Returns `null` when the
 * response isn't in ProblemDetails shape (so the caller can preserve
 * its existing message extraction path).
 */
function extractProblemDetailsValidationMessages(
  errorData: unknown
): { raw: ProblemDetailsValidationEntry[]; joined: string } | null {
  if (!errorData || typeof errorData !== 'object') return null;
  const extensions = (errorData as { extensions?: { validationErrors?: unknown } }).extensions;
  const entries = extensions?.validationErrors;
  if (!Array.isArray(entries) || entries.length === 0) return null;

  const typedEntries = entries as ProblemDetailsValidationEntry[];
  const collected: string[] = [];

  const walk = (entry: ProblemDetailsValidationEntry) => {
    if (entry?.constraints) {
      for (const value of Object.values(entry.constraints)) {
        if (typeof value === 'string') collected.push(value);
      }
    }
    if (Array.isArray(entry?.children)) {
      for (const child of entry.children) walk(child);
    }
  };
  for (const entry of typedEntries) walk(entry);

  if (collected.length === 0) return null;
  return { raw: typedEntries, joined: collected.join(', ') };
}

/**
 * Details returned by backend when subscription limit is exceeded
 * Matches LimitEnforcementInterceptor response format
 */
export interface SubscriptionLimitErrorDetails {
  resource: string;
  current: number;
  limit: number;
  upgradeUrl: string;
}

export interface ErrorResponse {
  message: string | Record<string, unknown>;
  code?: string;
  status?: number;
  errors?: ValidationError[];
  requestId?: string;
  timestamp?: string;
  // NestJS problem-details may use `detail` instead of `message`
  detail?: string | Record<string, unknown>;
  // Subscription limit specific fields (from LimitEnforcementInterceptor)
  resource?: string;
  current?: number;
  limit?: number;
  upgradeUrl?: string;
}

// Error messages in Turkish and English - Deprecated, use translations instead
const _errorMessages = {
  tr: {
    [ErrorType.NETWORK]: 'Ağ bağlantısı hatası. Lütfen internet bağlantınızı kontrol edin.',
    [ErrorType.AUTHENTICATION]: 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.',
    [ErrorType.AUTHORIZATION]: 'Bu işlem için yetkiniz bulunmamaktadır.',
    [ErrorType.VALIDATION]: 'Girdiğiniz bilgilerde hata var. Lütfen kontrol edin.',
    [ErrorType.NOT_FOUND]: 'Aradığınız kayıt bulunamadı.',
    [ErrorType.RATE_LIMIT]: 'Çok fazla istek gönderdiniz. Lütfen biraz bekleyin.',
    [ErrorType.SUBSCRIPTION_LIMIT]:
      'Abonelik limitinize ulaştınız. Devam etmek için planınızı yükseltin.',
    [ErrorType.SERVER]: 'Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.',
    [ErrorType.CLIENT]: 'İstemci hatası oluştu. Lütfen sayfayı yenileyin.',
    [ErrorType.UNKNOWN]: 'Beklenmeyen bir hata oluştu.',
  },
  en: {
    [ErrorType.NETWORK]: 'Network connection error. Please check your internet connection.',
    [ErrorType.AUTHENTICATION]: 'Authentication error. Please login again.',
    [ErrorType.AUTHORIZATION]: 'You do not have permission for this action.',
    [ErrorType.VALIDATION]: 'There are errors in the information you entered. Please check.',
    [ErrorType.NOT_FOUND]: 'The requested resource was not found.',
    [ErrorType.RATE_LIMIT]: 'Too many requests. Please wait a moment.',
    [ErrorType.SUBSCRIPTION_LIMIT]:
      'You have reached your subscription limit. Please upgrade your plan to continue.',
    [ErrorType.SERVER]: 'Server error occurred. Please try again later.',
    [ErrorType.CLIENT]: 'Client error occurred. Please refresh the page.',
    [ErrorType.UNKNOWN]: 'An unexpected error occurred.',
  },
};

// Error handler class
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorListeners: ((error: AppError) => void)[] = [];
  private reportEndpoint = '/api/v1/errors';
  private reportingEnabled = true;

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  setReportingEnabled(enabled: boolean) {
    this.reportingEnabled = enabled;
  }

  setReportEndpoint(endpoint: string) {
    this.reportEndpoint = endpoint;
  }

  // Subscribe to error events
  onError(listener: (error: AppError) => void) {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter((l) => l !== listener);
    };
  }

  // Determine error type from status code
  private getErrorType(status?: number): ErrorType {
    if (!status) return ErrorType.UNKNOWN;

    if (status >= 400 && status < 500) {
      switch (status) {
        case 401:
          return ErrorType.AUTHENTICATION;
        case 402:
          return ErrorType.SUBSCRIPTION_LIMIT;
        case 403:
          return ErrorType.AUTHORIZATION;
        case 404:
          return ErrorType.NOT_FOUND;
        case 422:
        case 400:
          return ErrorType.VALIDATION;
        case 429:
          return ErrorType.RATE_LIMIT;
        default:
          return ErrorType.CLIENT;
      }
    } else if (status >= 500) {
      return ErrorType.SERVER;
    }

    return ErrorType.UNKNOWN;
  }

  // Parse axios error
  parseAxiosError(error: AxiosError<ErrorResponse>): AppError {
    // Network error
    if (error.code === 'ERR_NETWORK' || !error.response) {
      return {
        type: ErrorType.NETWORK,
        message: ErrorType.NETWORK,
        timestamp: new Date().toISOString(),
        code: error.code,
      };
    }

    const { response } = error;
    const errorType = this.getErrorType(response.status);

    // Extract error details from response
    const errorData = response.data;

    // B-103 + B-088 + B-097 + … : NestJS global filter emits ProblemDetails
    // with the structured per-field reasons under
    // `extensions.validationErrors[*].constraints.*`. The previous parser
    // only looked at `message`/`detail`, both of which the filter sets to a
    // generic "Validation error" string — so every form caught a useless
    // toast and the tester had no way to see which field failed. Pull the
    // constraint messages out and concatenate them so getErrorMessage()
    // returns something actionable, while still keeping the raw array in
    // `details.validationErrors` for callers that want structured access.
    const problemValidationErrors = extractProblemDetailsValidationMessages(errorData);

    // RULE 15: Backend may return message as object (NestJS problem-details).
    // Always coerce to string — never pass [object Object] to the UI.
    const rawMessage = errorData?.message ?? errorData?.detail ?? errorType;
    const coercedMessage =
      typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
    const message =
      problemValidationErrors && coercedMessage === 'Validation error'
        ? problemValidationErrors.joined
        : coercedMessage;

    // Build details object based on error type
    let details: Record<string, unknown> | undefined;

    if (errorType === ErrorType.SUBSCRIPTION_LIMIT && errorData) {
      // Include subscription limit specific details
      details = {
        resource: errorData.resource,
        current: errorData.current,
        limit: errorData.limit,
        upgradeUrl: errorData.upgradeUrl,
      };
    } else if (errorData?.errors) {
      details = { validationErrors: errorData.errors };
    } else if (problemValidationErrors) {
      details = { validationErrors: problemValidationErrors.raw };
    }

    return {
      type: errorType,
      message,
      code: errorData?.code || error.code,
      status: response.status,
      details,
      timestamp: errorData?.timestamp || new Date().toISOString(),
      requestId: errorData?.requestId,
      path: error.config?.url,
      method: error.config?.method?.toUpperCase(),
    };
  }

  // Handle any error
  handle(error: unknown): AppError {
    let appError: AppError;

    if (error instanceof Error) {
      if ('isAxiosError' in error && error.isAxiosError) {
        appError = this.parseAxiosError(error as AxiosError<ErrorResponse>);
      } else {
        // Regular JavaScript error
        appError = {
          type: ErrorType.CLIENT,
          message: error.message,
          timestamp: new Date().toISOString(),
          details: {
            stack: error.stack,
          },
        };
      }
    } else if (typeof error === 'string') {
      appError = {
        type: ErrorType.UNKNOWN,
        message: error,
        timestamp: new Date().toISOString(),
      };
    } else {
      appError = {
        type: ErrorType.UNKNOWN,
        message: ErrorType.UNKNOWN,
        timestamp: new Date().toISOString(),
        details: error as Record<string, unknown>,
      };
    }

    // Notify listeners
    this.notifyListeners(appError);

    // Report to backend
    if (this.reportingEnabled && this.shouldReport(appError)) {
      this.reportError(appError);
    }

    return appError;
  }

  // Notify all error listeners
  private notifyListeners(error: AppError) {
    this.errorListeners.forEach((listener) => {
      try {
        listener(error);
      } catch (e) {
        console.error('Error in error listener:', e);
      }
    });
  }

  // Check if error should be reported
  private shouldReport(error: AppError): boolean {
    // Don't report client validation errors or 404s
    if (error.type === ErrorType.VALIDATION || error.type === ErrorType.NOT_FOUND) {
      return false;
    }

    // Get environment safely
    const nodeEnv =
      typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : 'production';

    // Don't report in development unless it's a server error
    if (nodeEnv === 'development' && error.type !== ErrorType.SERVER) {
      return false;
    }

    return true;
  }

  // Report error to backend
  private async reportError(error: AppError) {
    try {
      // Don't use the main API client to avoid circular dependencies
      await fetch(this.reportEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...error,
          userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
          environment:
            typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : 'browser',
        }),
      });
    } catch (reportError) {
      console.error('Failed to report error:', reportError);
    }
  }

  // Get user-friendly error message
  getUserMessage(error: AppError): string {
    if (error.type === ErrorType.VALIDATION && error.details?.validationErrors) {
      const errors = error.details.validationErrors as ValidationError[];
      return errors.map((e) => `${e.field}: ${e.message}`).join('\n');
    }

    return error.message;
  }

  // Clear all errors from React Query cache
  clearQueryErrors() {
    queryClient.resetQueries();
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Helper functions
export function handleError(error: unknown): AppError {
  return errorHandler.handle(error);
}

/**
 * Extract HTTP status from a thrown error of unknown shape. Handles
 * three cases:
 *   1. The api-client interceptor's `AppError` (`.status` set directly)
 *   2. A raw axios error (`.response.status`)
 *   3. Anything else → `undefined`
 *
 * Use to branch on specific status codes (403/404/422/etc.) without
 * re-doing the cast dance at every catch site.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (error == null || typeof error !== 'object') return undefined;
  const direct = (error as { status?: unknown }).status;
  if (typeof direct === 'number') return direct;
  const nested = (error as { response?: { status?: unknown } }).response?.status;
  if (typeof nested === 'number') return nested;
  return undefined;
}

/**
 * Extract a per-field validation-errors array from a thrown error of
 * unknown shape. Looks in three places, in order:
 *   1. `error.details.validationErrors` — populated by
 *      `parseAxiosError` when the response body has `errors: [...]`
 *      (typically a 422 from a NestJS controller throwing
 *      `UnprocessableEntityException({ errors })`)
 *   2. `error.response.data.errors` — the raw axios shape, in case
 *      the api-client interceptor was bypassed
 *   3. Anything else → `null`
 *
 * Generic over the per-error shape so callers narrow it themselves
 * (`FilingValidationError[]`, `ClaimEvidenceError[]`, …).
 *
 * Returns `null` (not `[]`) when no validation-errors are present so
 * callers can `if (errors)` to distinguish "hit by 422" from "hit by
 * 500 / 404 / network error".
 */
export function getValidationErrors<TError = unknown>(error: unknown): readonly TError[] | null {
  if (error == null || typeof error !== 'object') return null;
  const fromDetails = (error as { details?: { validationErrors?: unknown } }).details
    ?.validationErrors;
  if (Array.isArray(fromDetails) && fromDetails.length > 0) {
    return fromDetails as readonly TError[];
  }
  const fromResponseData = (error as { response?: { data?: { errors?: unknown } } }).response?.data
    ?.errors;
  if (Array.isArray(fromResponseData) && fromResponseData.length > 0) {
    return fromResponseData as readonly TError[];
  }
  return null;
}

// Deprecated - use translations instead
export function getUserErrorMessage(error: unknown): string {
  const appError = handleError(error);
  return appError.message;
}

// Global error event handlers
if (typeof window !== 'undefined') {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    // Check if reason exists and is not null
    if (event.reason != null) {
      console.error('Unhandled promise rejection:', event.reason);
      errorHandler.handle(event.reason);
    } else {
      console.warn('Unhandled promise rejection with null reason:', event);
    }
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    // Check if error exists and is not null
    if (event.error != null) {
      console.error('Global error:', event.error);
      errorHandler.handle(event.error);
    } else {
      console.warn('Global error event with null error:', event);
    }
  });
}
